import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  RefreshCw, Copy, CheckCircle, AlertCircle, Play, ChevronDown, ChevronUp,
  Terminal, Search, Eye, Edit, Database, Zap, ExternalLink,
  Cloud, Upload, FolderPlus, Download, Bell, FileText
} from 'lucide-react';
import {
  apiService,
  MCPStatusResponse,
  MCPToolsResponse,
  MCPToolDefinition,
} from '../services/api';
import { commitOwnedSearch } from '../navigation/historyState';
import { PRODUCT_NAME } from '../brand';
import McpToolSidebar from './McpToolSidebar';
import { useTabActivity } from '../contexts/TabActivityContext';
import './McpTab.css';

const MCP_SEARCH_KEYS = ['profile', 'tool', 'platform', 'apiKey'];

// Category icons
const CATEGORY_ICONS: Record<string, typeof Search> = {
  'Search': Search,
  'Discovery': Eye,
  'Video': Play,
  'Processing': Zap,
  'Prompts': FileText,
  'Connectors': Cloud,
  'Import': Upload,
  'Index': FolderPlus,
  'Export': Download,
  'Webhooks': Bell,
  'Workflow': Zap,
  'Prompt runs': Play,
  'Import jobs': Upload
};

const McpTab: React.FC = () => {
  const [status, setStatus] = useState<MCPStatusResponse | null>(null);
  const [tools, setTools] = useState<MCPToolsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('mcpToolSidebarWidth');
    return saved ? parseInt(saved, 10) : 600;
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { isActive } = useTabActivity();
  const params = new URLSearchParams(location.search);
  const selectedTool = tools?.tools.find(tool => tool.name === params.get('tool')) || null;
  const showSidebar = selectedTool !== null;
  const visibleTools = useMemo(() => tools?.tools || [], [tools]);
  const visibleCategories = useMemo(() => Object.entries(tools?.categories || {})
    .map(([category, names]) => ({
      category,
      tools: visibleTools.filter(tool => names.includes(tool.name)),
    }))
    .filter(category => category.tools.length > 0), [tools, visibleTools]);

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    tools: true,
    examples: false
  });

  // Expanded category state
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const loadInitialData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statusData, toolsData] = await Promise.all([
        apiService.getMcpStatus(),
        apiService.getMcpTools()
      ]);
      setStatus(statusData);
      setTools(toolsData);

      // Expand all categories by default
      if (toolsData?.categories) {
        const expanded: Record<string, boolean> = {};
        Object.keys(toolsData.categories).forEach(cat => {
          expanded[cat] = true;
        });
        setExpandedCategories(expanded);
      }
    } catch (err: any) {
      console.error('Failed to load MCP data:', err);
      setError(err.response?.data?.error?.message || 'Failed to load MCP tools');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInitialData();
  }, [loadInitialData]);

  // A retained inactive tab must not rewrite the active tab's URL.
  useEffect(() => {
    if (!isActive || !tools) return;
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: MCP_SEARCH_KEYS,
      updates: { profile: null, tool: selectedTool?.name || null },
      mode: 'replace',
    });
  }, [isActive, location, navigate, selectedTool?.name, tools]);

  const changeSelection = (tool: MCPToolDefinition | null) => {
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: MCP_SEARCH_KEYS,
      updates: { profile: null, tool: tool?.name || null },
      mode: 'push',
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Listen for sidebar width changes
  useEffect(() => {
    const handleSidebarWidthChange = (event: CustomEvent) => {
      setSidebarWidth(event.detail.width);
    };

    window.addEventListener('mcpToolSidebarWidthChange' as any, handleSidebarWidthChange);

    return () => {
      window.removeEventListener('mcpToolSidebarWidthChange' as any, handleSidebarWidthChange);
    };
  }, []);

  const handleSelectTool = (tool: MCPToolDefinition) => changeSelection(tool);
  const handleCloseSidebar = () => changeSelection(null);

  // Calculate container margin based on sidebar
  const containerStyle = useMemo(() => {
    if (showSidebar) {
      return { marginRight: `${sidebarWidth}px` };
    }
    return {};
  }, [showSidebar, sidebarWidth]);

  if (isLoading) {
    return (
      <div className="mcp-tab">
        <div className="mcp-loading">
          <RefreshCw size={24} className="spinning" />
          <span>Loading MCP tools...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mcp-tab">
        <div className="mcp-error">
          <AlertCircle size={24} />
          <span>{error}</span>
          <button onClick={loadInitialData} className="retry-btn">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mcp-tab">
      <div className="mcp-container" style={containerStyle}>
        {/* Header */}
        <div className="mcp-header">
          <div className="mcp-header-content">
            <div className="mcp-header-icon">
              <Terminal size={32} />
            </div>
            <div className="mcp-header-text">
              <h1>MCP Server Integration</h1>
              <p>Connect {PRODUCT_NAME} to AI assistants using the Model Context Protocol</p>
            </div>
          </div>
          <div className="mcp-header-stats">
            <div className="stat">
              <span className="stat-value" aria-label={`${visibleTools.length} tools in this view`}>{visibleTools.length}</span>
              <span className="stat-label">Tools</span>
            </div>
            <div className="stat">
              <span className="stat-value">v{status?.server_version || '1.0.0'}</span>
              <span className="stat-label">Version</span>
            </div>
          </div>
        </div>

        <p className="mcp-tool-view-description">Six tools cover analysis, search, imports, reusable prompts, and browsing your library.</p>

        {/* Available Tools Section - Now First */}
        <div className="mcp-section">
          <div
            className="mcp-section-header"
            onClick={() => toggleSection('tools')}
          >
            <div className="section-title-group">
              <Database size={20} />
              <h2>Available Tools</h2>
              <span className="section-badge">{visibleTools.length} tools</span>
            </div>
            {expandedSections.tools ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>

          {expandedSections.tools && tools && (
            <div className="mcp-section-content">
              {visibleTools.length === 0 && <p className="tools-intro">No tools available in this view.</p>}
              {/* Tools by Category */}
              {visibleCategories.map(({ category, tools: categoryTools }) => {
                const CategoryIcon = CATEGORY_ICONS[category] || Database;
                const isExpanded = expandedCategories[category];

                return (
                  <div key={category} className="tool-category">
                    <div
                      className="category-header"
                      onClick={() => toggleCategory(category)}
                    >
                      <CategoryIcon size={18} />
                      <span className="category-name">{category}</span>
                      <span className="category-count">{categoryTools.length}</span>
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>

                    {isExpanded && (
                      <div className="category-tools">
                        {categoryTools.map(tool => (
                          <button
                            key={tool.name}
                            type="button"
                            aria-label={`Try ${tool.name}`}
                            className={`tool-card ${selectedTool?.name === tool.name ? 'selected' : ''}`}
                            onClick={() => handleSelectTool(tool)}
                          >
                            <span className="tool-name">{tool.name}</span>
                            <span className="tool-description">
                              {tool.description.split('\n')[0]}
                            </span>
                            <span className="tool-params">
                              {tool.inputSchema.required?.map(param => (
                                <span key={param} className="param required">{param}</span>
                              ))}
                              {Object.keys(tool.inputSchema.properties || {})
                                .filter(p => !tool.inputSchema.required?.includes(p))
                                .slice(0, 3)
                                .map(param => (
                                  <span key={param} className="param optional">{param}</span>
                                ))}
                            </span>
                            <span className="try-btn" aria-hidden="true">
                              <Play size={12} />
                              Try
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Example Prompts Section */}
        <div className="mcp-section">
          <div
            className="mcp-section-header"
            onClick={() => toggleSection('examples')}
          >
            <div className="section-title-group">
              <Edit size={20} />
              <h2>Example Prompts</h2>
              <span className="section-badge">Ready to use</span>
            </div>
            {expandedSections.examples ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>

          {expandedSections.examples && (
            <div className="mcp-section-content">
              <p className="examples-intro">
                Copy these prompts to use with Claude or other AI assistants connected to your {PRODUCT_NAME} MCP server.
              </p>

              <div className="examples-grid">
                <ExamplePromptCard
                  title="Search Videos"
                  prompt="Search my video library for scenes showing people shaking hands"
                  description="Uses semantic search to find relevant video segments"
                />
                <ExamplePromptCard
                  title="List Available Indexes"
                  prompt="What video indexes do I have access to? Show me the list."
                  description="Discovers available video collections"
                />
                <ExamplePromptCard
                  title="Analyze Video Content"
                  prompt="Process all videos in my Marketing index with the Scene Analysis prompt and let me know when it's done"
                  description="Executes prompt analysis on videos"
                />
                <ExamplePromptCard
                  title="Get Video Details"
                  prompt="Show me the details and segments from video vid_abc123"
                  description="Retrieves video metadata and extracted content"
                />
                <ExamplePromptCard
                  title="Filter by Metadata"
                  prompt="Find all segments in my index where the object_type is 'vehicle'"
                  description="Uses metadata filtering for precise results"
                />
                <ExamplePromptCard
                  title="Check Processing Status"
                  prompt="Check analysis anl_xyz789 and save its completed JSON and segment clips."
                  description="Reads the same analysis without rerunning it"
                />
                <ExamplePromptCard
                  title="Import from Cloud"
                  prompt="Create an import job to bring all MP4 files from my S3 connector into the Production index"
                  description="Imports videos from cloud storage"
                />
                <ExamplePromptCard
                  title="Save a Reusable Prompt"
                  prompt="Save a reusable prompt that describes the main objects and actions in each segment"
                  description="Creates or updates a saved prompt"
                />
              </div>
            </div>
          )}
        </div>

        {/* Resources Footer */}
        <div className="mcp-resources">
          <a href="https://vectormethods.com/docs/mcp" target="_blank" rel="noopener noreferrer">
            <ExternalLink size={14} />
            Documentation
          </a>
          <a href="https://www.npmjs.com/package/@vectormethods/videovector-mcp-server" target="_blank" rel="noopener noreferrer">
            <ExternalLink size={14} />
            NPM Package
          </a>
          <a href="https://modelcontextprotocol.io" target="_blank" rel="noopener noreferrer">
            <ExternalLink size={14} />
            MCP Protocol
          </a>
        </div>
      </div>

      {/* Tool Detail Sidebar */}
      <McpToolSidebar
        tool={selectedTool}
        isOpen={showSidebar}
        onClose={handleCloseSidebar}
      />
    </div>
  );
};

// Example Prompt Card Component
interface ExamplePromptCardProps {
  title: string;
  prompt: string;
  description: string;
}

const ExamplePromptCard: React.FC<ExamplePromptCardProps> = ({ title, prompt, description }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="example-card">
      <div className="example-header">
        <h4>{title}</h4>
        <button className={`copy-btn-small ${copied ? 'copied' : ''}`} onClick={handleCopy}>
          {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
        </button>
      </div>
      <blockquote className="example-prompt">{prompt}</blockquote>
      <p className="example-description">{description}</p>
    </div>
  );
};

export default McpTab;
